import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  IoHomeOutline,
  IoPulseOutline,
  IoGridOutline,
  IoStatsChartOutline,
  IoBookmarkOutline,
  IoListOutline,
  IoKeyOutline,
  IoClose,
} from "react-icons/io5";
import NavBar from "../../components/common/NavBar";
import SavedAreas from "../../components/areas/SavedAreas";
import ProductCards from "../../components/home/ProductCards";
import DashboardsHome from "../../components/home/DashboardsHome";
import JobsList from "../../components/home/JobsList";
import ApiKeysManager from "../../components/home/ApiKeysManager";
import HomeLanding from "../../components/home/HomeLanding";
import StatusSection from "../../components/status/StatusSection";
import { isLegacyAreasKey, parseSection } from "./homeSection";
import type { Section } from "./homeSection";

const HomeIcon = IoHomeOutline as any;
const PulseIcon = IoPulseOutline as any;
const GridIcon = IoGridOutline as any;
const DashboardIcon = IoStatsChartOutline as any;
const BookmarkIcon = IoBookmarkOutline as any;
const ListIcon = IoListOutline as any;
const KeyIcon = IoKeyOutline as any;
const CloseIcon = IoClose as any;

interface NavItem {
  key: Section;
  label: string;
  icon: any;
  group: "primary" | "developer";
}

const NAV_ITEMS: NavItem[] = [
  { key: "home", label: "Home", icon: HomeIcon, group: "primary" },
  { key: "dashboards", label: "Dashboards", icon: DashboardIcon, group: "primary" },
  { key: "products", label: "Products", icon: GridIcon, group: "primary" },
  { key: "areas", label: "Areas", icon: BookmarkIcon, group: "primary" },
  { key: "jobs", label: "Your Jobs", icon: ListIcon, group: "primary" },
  { key: "status", label: "Status", icon: PulseIcon, group: "primary" },
  { key: "developer", label: "Developer", icon: KeyIcon, group: "developer" },
];

const HomePage: React.FC = () => {
  // The section is in the URL (`/?section=status`), so other pages can link to one and Back works.
  const [params, setParams] = useSearchParams();
  const sectionParam = params.get("section");
  const activeSection = parseSection(sectionParam);
  // The old Saved Areas and Create New Area keys now mean Areas: rewrite the URL in place.
  useEffect(() => {
    if (isLegacyAreasKey(sectionParam)) setParams({ section: "areas" }, { replace: true });
  }, [sectionParam, setParams]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const renderSection = () => {
    switch (activeSection) {
      case "home":
        return <HomeLanding onNavigate={handleSectionChange} />;
      case "dashboards":
        return <DashboardsHome />;
      case "products":
        return <ProductCards />;
      case "areas":
        return <SavedAreas />;
      case "jobs":
        return <JobsList />;
      case "status":
        return <StatusSection />;
      case "developer":
        return <ApiKeysManager />;
      default:
        return <HomeLanding onNavigate={handleSectionChange} />;
    }
  };

  const handleSectionChange = (section: Section) => {
    setParams(section === "home" ? {} : { section });
    setIsDrawerOpen(false);
    window.scrollTo(0, 0);
  };

  const renderNavGroup = (group: "primary" | "developer") => (
    <ul className="space-y-1">
      {NAV_ITEMS.filter((item) => item.group === group).map((item) => {
        const Icon = item.icon;
        const isActive = activeSection === item.key;
        return (
          <li key={item.key}>
            <button
              onClick={() => handleSectionChange(item.key)}
              className={`group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                isActive
                  ? "bg-brand-50 text-brand-700"
                  : "text-ink-muted hover:bg-paper-200 hover:text-ink"
              }`}
            >
              <Icon
                className={`h-5 w-5 ${
                  isActive ? "text-brand-500" : "text-ink-faint group-hover:text-ink-muted"
                }`}
              />
              <span>{item.label}</span>
              {isActive && (
                <span className="ml-auto h-1.5 w-1.5 rounded-full bg-brand-500" />
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="min-h-screen bg-paper-200">
      <NavBar
        variant="brand"
        showMenuButton={true}
        onMenuClick={() => setIsDrawerOpen(!isDrawerOpen)}
      />

      <div className="mx-auto flex max-w-[1600px] gap-6 px-4 py-6 sm:px-6 lg:px-8">
        {/* Desktop persistent sidebar */}
        <aside className="hidden lg:block w-64 flex-shrink-0">
          <div className="sticky top-[80px] rounded-2xl border border-paper-300/70 bg-surface p-4 shadow-soft">
            <p className="px-3 py-2 text-[11px] font-mono uppercase tracking-widest text-ink-faint">
              Workbench
            </p>
            {renderNavGroup("primary")}
            <p className="mt-6 px-3 py-2 text-[11px] font-mono uppercase tracking-widest text-ink-faint">
              Developer
            </p>
            {renderNavGroup("developer")}
          </div>
        </aside>

        {/* Mobile drawer */}
        {isDrawerOpen && (
          <div
            className="fixed inset-0 bg-scrim/40 z-40 lg:hidden animate-fade-in"
            onClick={() => setIsDrawerOpen(false)}
          />
        )}
        <aside
          className={`fixed top-0 left-0 h-full w-72 bg-surface z-50 transform transition-transform duration-300 ease-brand lg:hidden shadow-2xl ${
            isDrawerOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex h-16 items-center justify-between border-b border-paper-300/70 px-4">
            <span className="text-sm font-mono uppercase tracking-widest text-ink-faint">
              Navigation
            </span>
            <button
              onClick={() => setIsDrawerOpen(false)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full text-ink-muted hover:bg-paper-200"
              aria-label="Close menu"
            >
              <CloseIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="p-4">
            <p className="px-3 py-2 text-[11px] font-mono uppercase tracking-widest text-ink-faint">
              Workbench
            </p>
            {renderNavGroup("primary")}
            <p className="mt-6 px-3 py-2 text-[11px] font-mono uppercase tracking-widest text-ink-faint">
              Developer
            </p>
            {renderNavGroup("developer")}
          </div>
        </aside>

        {/* Main content */}
        <main className="min-w-0 flex-1 animate-fade-in">{renderSection()}</main>
      </div>
    </div>
  );
};

export default HomePage;
